import React, { useEffect, useState } from 'react';

// 🌸 Élodie #831 — Dialog custom promise-based (fini les window.confirm/alert moches)
// Usage :
//   const ok = await window.showConfirm('Supprimer ce RDV ?')
//   await window.showAlert('Erreur 💕')
// Le composant AppConfirmHost DOIT être monté 1x dans App.js à la racine.

let _pushDialog = null; // setter du host

function pushDialog(opts) {
  return new Promise((resolve) => {
    if (!_pushDialog) {
      // fallback si host non monté (SSR, initialisation) — n'utilise PAS window.confirm/alert
      resolve(opts.type === 'confirm' ? false : true);
      return;
    }
    _pushDialog({ ...opts, resolve, id: Date.now() + Math.random() });
  });
}

// Globals (utilisables partout, y compris dans les fichiers .js)
if (typeof window !== 'undefined') {
  window.showConfirm = (message, { title, okText = 'Oui', cancelText = 'Annuler', danger = true } = {}) =>
    pushDialog({ type: 'confirm', message: String(message ?? ''), title, okText, cancelText, danger });
  window.showAlert = (message, { title, okText = 'OK' } = {}) =>
    pushDialog({ type: 'alert', message: String(message ?? ''), title, okText, cancelText: null, danger: false });
}

export function AppConfirmHost() {
  const [dialog, setDialog] = useState(null);
  useEffect(() => { _pushDialog = setDialog; return () => { _pushDialog = null; }; }, []);
  if (!dialog) return null;

  const close = (result) => {
    dialog.resolve(result);
    setDialog(null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => close(dialog.type === 'confirm' ? false : true)} />
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl border-2 border-[#F5DDE5] overflow-hidden animate-scale-in" data-testid="app-confirm-dialog">
        {/* Halo décoratif girly */}
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#F5A5C4]/25 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[#B399E6]/25 blur-2xl pointer-events-none" />
        <div className="relative p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#F5A5C4] to-[#B399E6] flex items-center justify-center flex-shrink-0 shadow-md">
              <span className="text-2xl">{dialog.type === 'confirm' ? (dialog.danger ? '💕' : '❔') : '💕'}</span>
            </div>
            <div className="flex-1">
              {dialog.title && <p className="text-sm font-bold text-[#5C3D36]">{dialog.title}</p>}
            </div>
          </div>
          <p className="text-[13px] text-[#5C3D36] leading-relaxed whitespace-pre-line mb-5">{dialog.message}</p>
          <div className="flex gap-2">
            {dialog.cancelText && (
              <button
                onClick={() => close(false)}
                className="flex-1 py-2.5 rounded-full bg-[#F5EBE0] text-[#5C3D36] text-xs font-semibold active:scale-95 hover:bg-[#EDDBCE] transition"
                data-testid="app-confirm-cancel"
              >
                {dialog.cancelText}
              </button>
            )}
            <button
              onClick={() => close(true)}
              className={`flex-1 py-2.5 rounded-full text-white text-xs font-bold shadow-md active:scale-95 transition ${
                dialog.danger
                  ? 'bg-gradient-to-r from-[#F5A5C4] to-[#B399E6] hover:shadow-lg'
                  : 'bg-gradient-to-r from-[#FFB5A7] to-[#FF8FA3] hover:shadow-lg'
              }`}
              data-testid="app-confirm-ok"
            >
              {dialog.okText}
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.18s ease-out both; }
      `}</style>
    </div>
  );
}

export default AppConfirmHost;
